import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import NiceModal from "@ebay/nice-modal-react";
import { FieldArray, FieldArrayRenderProps, Form, Formik } from "formik";
import {
  GameConfigs,
  type Config,
  GameConfig,
  ActiveEvent,
  GameTypes,
  isPerformanceGame
} from "kadow-jetpack-model";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../designsystem/KC_Button/KC_Button";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../../redux/jetpackConfig";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { type StepProps } from "../../JetpackOnboarding";
import GameCardPreview from "../../../23_GameCardPreview/GameCardPreview";
import KC_Card from "../../../../designsystem/KC_Card/KC_Card";
import { useEventsSelector } from "../../../../redux/actions/wheel";
import { GameThumbnailDTO } from "../../../../model/GameDTO";
import { buildGamesListForOnboarding } from "../../../../utils/helpers/buildGamesListForOnboarding";
import Carousel from "../../../../components/Carousel/Carousel";
import { tt } from "../../../../utils/i18n/i18n";
import { getDisplayNameForGameType } from "../../../../utils/helpers/getDisplayNameForGameType";
import UpsellContainer from "../../../19_JetpackConfig/components/UpsellComponents/UpsellContainer";
import PlanOptionModal from "../../../../components/modal/PlanOption/PlanOptionModal";
import { KC_PaddingEnum } from "../../../../designsystem/KC_Card/component/model/KC_PaddingEnum";
import { getDefaultGameConfig } from "../../../../helpers/getDefaultGameConfig";
import "../styles.scss";

export default function GamesSelectorOnboarding(props: StepProps) {
  const dispatch = useDispatch();

  const jetpackConfig = useJetpackConfigSelector();
  const events = useEventsSelector();

  const { accountToDisplay } = useAccountToDisplaySelector();

  const sliderContainerRef = useRef<HTMLDivElement>(null);

  const subscriptionAllowsGameChoice = useMemo(() => {
    return (
      accountToDisplay?.saasFeatures?.pg || accountToDisplay?.saasFeatures?.gc
    );
  }, [accountToDisplay?.saasFeatures?.pg, accountToDisplay?.saasFeatures?.gc]);

  const handleSubmit = (values: Config) => {
    dispatch(
      updateJetpackConfigThunk(
        values,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
    props.nextStep();
  };

  const scrollToSecondItemOnMobileDevice = () => {
    const sliderContainer = sliderContainerRef.current;
    if (!sliderContainer) return;

    const items = sliderContainer.querySelectorAll(".gallery-item");
    if (items.length < 2) return;

    const firstItem = items[0] as HTMLElement;
    const secondItem = items[1] as HTMLElement;

    firstItem.classList.remove("current");
    secondItem.classList.add("current");

    const scrollOptions = {
      left: secondItem.offsetLeft - sliderContainer.offsetLeft,
      behavior: "smooth" as ScrollBehavior
    };

    sliderContainer.scrollTo(scrollOptions);
  };

  const toggleGame = (
    gtb: GameThumbnailDTO,
    setFieldValue: (name: string, val: any) => void,
    push: (obj: GameConfig) => void,
    remove: (index: number) => void,
    isSelected: boolean,
    values: Config
  ) => {
    if (!gtb.name) {
      return;
    }

    if (isPerformanceGame(gtb.name as GameTypes) || gtb.eventId !== "default") {
      if (isSelected) {
        const index = values?.activeEvents?.findIndex(
          (configAE) =>
            configAE.id === gtb.eventId &&
            configAE.sts === (gtb.sts || -1) &&
            configAE.ets === (gtb.ets || -1) &&
            configAE.gameType === gtb.name
        );

        if (index != null && index >= 0) {
          const newArr = [...(values?.activeEvents || [])];
          newArr.splice(index, 1);
          setFieldValue("activeEvents", newArr);
        }
      } else {
        const newArr = [...(values?.activeEvents || [])];
        newArr.push({
          id: gtb.eventId,
          gameType: gtb.name,
          sts: gtb.sts || -1,
          ets: gtb.ets || -1
        });
        setFieldValue("activeEvents", newArr);
      }
    } else {
      if (isSelected) {
        const index = values?.games.findIndex(
          (game: GameConfig) => game.name === gtb.name
        );
        if (index != null && index >= 0) {
          removeGame(index, remove);
        }
      } else {
        addGame(gtb.name as keyof GameConfigs, push);
      }
    }
  };

  const removeGame = (index: number, remove: (index: number) => void) => {
    remove(index);
  };

  const addGame = (
    gameType: keyof GameConfigs,
    push: (obj: GameConfig) => void
  ) => {
    if (!gameType) {
      alert(tt("errorAlert"));
      return;
    }

    const newGame = {
      name: gameType,
      config: getDefaultGameConfig(gameType)
    };

    if (newGame.config != null) {
      newGame.config.description = "";
      //@ts-ignore
      push(newGame);
    }
  };

  const GAMES_LIST: GameThumbnailDTO[] = useMemo(() => {
    let result: GameThumbnailDTO[];

    result = buildGamesListForOnboarding();

    const fwIndex = result.findIndex(
      (item) => item.name === "fw" && item.eventId === "default"
    );

    if (fwIndex > 0) {
      const [item] = result.splice(fwIndex, 1);

      if (item) {
        result.unshift(item);
      }
    }
    return result;
  }, [
    accountToDisplay?.saasFeatures?.gc,
    accountToDisplay?.saasFeatures?.pg,
    events,
    jetpackConfig
  ]);

  function getSlides(
    values: Config,
    setFieldValue: (name: string, val: any) => void,
    push: (obj: GameConfig) => void,
    remove: (index: number) => void,
    className: string
  ) {
    const res: {
      games: ReactNode[];
      upsell: ReactNode[];
      upsellMobilePreview: ReactNode[];
    } = {
      games: [],
      upsell: [],
      upsellMobilePreview: []
    };

    GAMES_LIST.forEach((gtb) => {
      let isSelected = false;

      if (
        isPerformanceGame(gtb.name as GameTypes) ||
        gtb.eventId !== "default"
      ) {
        isSelected = !!values.activeEvents?.some(
          (ae: ActiveEvent) => ae.gameType === gtb.name && ae.id === gtb.eventId
        );
      } else {
        isSelected = values.games?.some((configGame: GameConfig) => {
          return configGame.name === gtb.name;
        });
      }

      let isUpsell = !isSelected;

      if (subscriptionAllowsGameChoice) {
        isUpsell = false;
      }

      if (isUpsell) {
        res.upsell.push(
          <KC_Card
            id={gtb.eventId}
            useMask={isUpsell}
            onMaskClick={() => {
              void NiceModal.show(PlanOptionModal);
            }}
            title={getDisplayNameForGameType(gtb.name)}
            logoURL={jetpackConfig?.logo_url}
            companyName={accountToDisplay?.placeName || ""}
            boxShadow={true}
            useTwicPics={true}
            isGameCardPreview={true}
            forceShowHeader={true}
            className={className}
            padding={KC_PaddingEnum.gift}
            onClick={() => {
              subscriptionAllowsGameChoice
                ? toggleGame(
                    gtb,
                    setFieldValue,
                    push,
                    remove,
                    isSelected,
                    values
                  )
                : null;
            }}
            isSelected={isSelected}
            children={
              <GameCardPreview
                gameCardConfig={gtb.config}
                eventId={gtb.eventId}
              />
            }
            canDelete={false}
          />
        );

        res.upsellMobilePreview.push(
          <GameCardPreview gameCardConfig={gtb.config} eventId={gtb.eventId} />
        );

        return;
      }

      res.games.push(
        <KC_Card
          id={gtb.eventId}
          isOnboarding
          useMask={isUpsell}
          onMaskClick={() => {
            void NiceModal.show(PlanOptionModal);
          }}
          title={getDisplayNameForGameType(gtb.name)}
          logoURL={jetpackConfig?.logo_url}
          companyName={accountToDisplay?.placeName}
          boxShadow={true}
          useTwicPics={true}
          isGameCardPreview={true}
          forceShowHeader={true}
          className={className}
          padding={KC_PaddingEnum.gift}
          onClick={() => {
            subscriptionAllowsGameChoice
              ? toggleGame(gtb, setFieldValue, push, remove, isSelected, values)
              : null;
          }}
          isSelected={isSelected}
          children={
            <GameCardPreview
              gameCardConfig={gtb.config}
              eventId={gtb.eventId}
            />
          }
          canDelete={true}
          handleDelete={() => {
            // toggleGame(gtb, setFieldValue, push, remove, isSelected, values);
          }}
        />
      );
    });

    return res;
  }

  useEffect(() => {
    const currentElement = document.querySelector(".gallery-item.current");

    if (currentElement != null) {
      return;
    }

    const sliderContainer = sliderContainerRef.current;

    if (sliderContainer != null) {
      if (window.innerWidth <= 768) {
        setTimeout(scrollToSecondItemOnMobileDevice, 100);
      } else {
        const firstElement = sliderContainer.querySelector(
          ".gallery-item"
        ) as HTMLElement;
        if (firstElement) {
          firstElement.classList.add("current");
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        scrollToSecondItemOnMobileDevice();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (sliderContainerRef.current) {
      sliderContainerRef.current.style.scrollBehavior = "smooth";
    }
  }, []);

  if (jetpackConfig == null) {
    return <h1>{tt("errorTitle")}</h1>;
  }

  return (
    <div id="game-selector" className="w-100">
      <Formik<Config>
        initialValues={jetpackConfig}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => {
          return (
            <Form className="w-100 form-game-selector">
              <FieldArray name="games">
                {({ push, remove }: FieldArrayRenderProps) => {
                  const slides = getSlides(
                    values,
                    setFieldValue,
                    push,
                    remove,
                    "flex-grow-1 flex-grow-sm-0 my-1 my-sm-0"
                  );

                  return (
                    <>
                      <div className="d-flex d-sm-none flex-column mb-5 st-gap game-selector-container-mobile">
                        {slides.games}
                        <UpsellContainer title={tt("upsell_game_title")}>
                          {slides.upsellMobilePreview}
                        </UpsellContainer>
                      </div>

                      <div className="game-selector-container d-none d-sm-block">
                        <Carousel
                          centerInsufficientSlides={true}
                          baseId="game-onboarding"
                          slides={[...slides.games, ...slides.upsell]}
                          style={{ height: "100%" }}
                        />
                      </div>
                    </>
                  );
                }}
              </FieldArray>
              <div
                className="d-flex d-sm-none position-sticky"
                style={{
                  bottom: 0,
                  right: 0,
                  left: 0
                }}
              >
                <KC_Button
                  text={tt("next_step_button")}
                  className="d-none d-sm-block submit-btn d-flex"
                  type={KC_ButtonType.Submit}
                  size={KC_ButtonSize.Giant}
                />
              </div>
              <div className="d-none d-sm-flex mx-auto game-selector-onboarding-btn">
                <KC_Button
                  id="submit-btn"
                  text={tt("next_step_button")}
                  className="d-none d-sm-block submit-btn d-flex"
                  type={KC_ButtonType.Submit}
                  size={KC_ButtonSize.Giant}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
